import { isNil } from "lodash";
import { useCallback, useEffect } from "react";
import {
  Issue,
  IssueCustomAttribute,
  IssueStandardFieldIds,
  IssueFieldId,
  IssueStatus,
  IssueType,
} from "@adsk/acc-issues-types";
import { useSelector } from "@adsk/acc-issues-core/state/redux/hooks";
import { getIssueTypeEntitiesMap } from "@adsk/acc-issues-core/state/redux/ducks/issueTypes";
import { useGetCustomFieldMappingsByIssueTypeId } from "./issueCustomFieldMappingHooks";
import { getSanitizedFieldList } from "../data/issueFieldOrdering";
import {
  getAreAllRequiredFieldsFilled,
  getIsIssueValueEmpty,
} from "../data/issueRequiredFields";
import { IssueFormAttributes } from "@adsk/acc-issues-core/utils/types/Issue";
import { convertCustomAttrObjToArray } from "../data/issueFieldSanitization";
import { useWatcherPropName } from "./watchers/useWatcherPropName";

const isRequiredIssueFieldUnfilled = ({
  fieldId,
  issue,
  watcherPropName,
}: {
  fieldId: IssueFieldId;
  issue: Issue;
  watcherPropName: "watchers" | "watcherObjects";
}) => {
  switch (fieldId) {
    case IssueStandardFieldIds.Description:
      return getIsIssueValueEmpty(issue.description);
    case IssueStandardFieldIds.Assignee:
      return getIsIssueValueEmpty(issue.assignedTo);
    case IssueStandardFieldIds.Watchers:
      return (
        getIsIssueValueEmpty(issue[watcherPropName]) ||
        !issue[watcherPropName]?.length
      );
    case IssueStandardFieldIds.Location:
      return getIsIssueValueEmpty(issue.locationId);
    case IssueStandardFieldIds.LocationDetails:
      return getIsIssueValueEmpty(issue.locationDetails);
    case IssueStandardFieldIds.DueDate:
      return getIsIssueValueEmpty(issue.dueDate);
    case IssueStandardFieldIds.StartDate:
      return getIsIssueValueEmpty(issue.startDate);
    case IssueStandardFieldIds.RootCauseWithCategory:
      return getIsIssueValueEmpty(issue.rootCauseId);
    default:
      const customAttribute = issue.customAttributes.find(
        ({ attributeDefinitionId }) => attributeDefinitionId === fieldId
      );
      return getIsIssueValueEmpty(customAttribute?.value);
  }
};

export const useGetUnfilledAndRequiredIssueFieldsMetadata = () => {
  const issueTypeEntitiesMap = useSelector(getIssueTypeEntitiesMap);
  const getCustomFieldMappingsByIssueTypeId =
    useGetCustomFieldMappingsByIssueTypeId();
  const watcherPropName = useWatcherPropName();

  return useCallback(
    ({ issueTypeId, issue }: { issueTypeId?: string; issue: Issue }) => {
      const issueType = issueTypeId
        ? issueTypeEntitiesMap[issueTypeId]
        : undefined;
      if (isNil(issueType)) {
        return [];
      }
      const fieldsMetadata = issueType.fieldsMetadata ?? [];
      const customFieldMappings = getCustomFieldMappingsByIssueTypeId(
        issueType.id
      );
      const customFieldMappingIds = customFieldMappings.map(
        mapping => mapping.attributeDefinitionId
      );
      const sanitizedFieldsMetadataList = getSanitizedFieldList({
        fieldList: fieldsMetadata,
        customFieldIdsForType: customFieldMappingIds,
      });
      return sanitizedFieldsMetadataList.filter(
        ({ id, required }: { id: string; required?: boolean }) => {
          return (
            !!required &&
            isRequiredIssueFieldUnfilled({
              fieldId: id,
              issue,
              watcherPropName,
            })
          );
        }
      );
    },
    [getCustomFieldMappingsByIssueTypeId, issueTypeEntitiesMap]
  );
};

export const useGetIsFieldRequiredByIssueTypeId = () => {
  const issueTypeEntitiesMap = useSelector(getIssueTypeEntitiesMap);

  return useCallback(
    ({
      issueFieldId,
      issueTypeId,
    }: {
      issueFieldId: IssueFieldId;
      issueTypeId: string;
    }) => {
      const issueType = issueTypeEntitiesMap[issueTypeId];
      const fieldsMetadata = issueType?.fieldsMetadata ?? [];
      return fieldsMetadata.some(
        ({ id, required }: { id: string; required?: boolean }) =>
          id === issueFieldId && !!required
      );
    },
    [issueTypeEntitiesMap]
  );
};

/**
 * The useEffect hook changes the issue's status if there are required fields that are unfilled.
 * Once the hideStatusField and hiddenStandardFields props are removed from the IssueCreationForm,
 * we can remove this hook.
 */
export const useChangeStatusWhenRequiredFieldsAreHidden = ({
  hideStatusField,
  areThereHiddenRequiredStandardFields,
  issueFormValues,
  selectedType,
  setFieldValue,
}: {
  hideStatusField: boolean | undefined;
  areThereHiddenRequiredStandardFields: boolean;
  issueFormValues: IssueFormAttributes;
  selectedType: IssueType | undefined;
  setFieldValue: (field: string, value: any) => void;
}) => {
  const formValuesStr = JSON.stringify(issueFormValues);
  const watcherProp = useWatcherPropName();

  useEffect(() => {
    if (hideStatusField || areThereHiddenRequiredStandardFields) {
      const { customAttributes, ...restOfIssue } = issueFormValues;
      let customAttrArray: IssueCustomAttribute[] = [];
      if (customAttributes) {
        customAttrArray = convertCustomAttrObjToArray(customAttributes);
      }
      const areAllRequiredFieldsFilled = getAreAllRequiredFieldsFilled({
        watcherProp,
        fieldsMetadata: selectedType?.fieldsMetadata || [],
        issue: {
          ...restOfIssue,
          customAttributes: customAttrArray,
        },
      });

      if (
        !areAllRequiredFieldsFilled &&
        issueFormValues.status !== IssueStatus.Draft
      ) {
        setFieldValue("status", IssueStatus.Draft);
      }
      if (
        areAllRequiredFieldsFilled &&
        issueFormValues.status === IssueStatus.Draft
      ) {
        setFieldValue("status", IssueStatus.Open);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    areThereHiddenRequiredStandardFields,
    hideStatusField,
    setFieldValue,
    formValuesStr,
  ]);
};
